import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth, useClerk } from '@clerk/clerk-react';
import { api } from '../utils/api';
import { useAdmin } from '../hooks/useAdmin';

function todayLocalIso() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export default function Admin() {
  const { getToken } = useAuth();
  const { signOut } = useClerk();
  const { isAdmin, loading: checking } = useAdmin();
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    vendorLimit: 10,
    address: '',
    daysOpen: '',
    operatingHours: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [createdMarket, setCreatedMarket] = useState(null);
  const [sales, setSales] = useState([]);
  const [earnings, setEarnings] = useState(null);
  const [salesLoading, setSalesLoading] = useState(false);
  const [earningsLoading, setEarningsLoading] = useState(false);
  const [reportsPeriod, setReportsPeriod] = useState('day');
  const [reportsDate, setReportsDate] = useState(todayLocalIso);
  const location = useLocation();
  const isReportsView = location.pathname === '/admin/reports';
  const [markets, setMarkets] = useState([]);
  const [marketsLoading, setMarketsLoading] = useState(false);
  const [selectedMarket, setSelectedMarket] = useState(null); // market detail for edit modal
  const [editForm, setEditForm] = useState({ name: '', code: '', vendorLimit: 10, address: '', daysOpen: '', operatingHours: '' });
  const [savingMarket, setSavingMarket] = useState(false);
  const [deletingMarket, setDeletingMarket] = useState(false);
  const [copiedOnboardingLink, setCopiedOnboardingLink] = useState(false);
  const [showCreateMarketForm, setShowCreateMarketForm] = useState(false);

  // Load markets when admin (for Dashboard tab). Refetch when opening Dashboard or after creating a market.
  useEffect(() => {
    if (!isAdmin) {
      setMarkets([]);
      return;
    }
    // Only fetch when on Dashboard so we have fresh list when user opens it
    if (location.pathname !== '/admin') return;

    let cancelled = false;
    (async () => {
      setMarketsLoading(true);
      try {
        const res = await api('/api/admin/markets', { getToken });
        const data = await res.json().catch(() => null);
        if (!cancelled) {
          const list = Array.isArray(data) ? data : (data?.data && Array.isArray(data.data) ? data.data : []);
          setMarkets(list);
          if (res.ok) setError(null);
          else if (list.length === 0) setError(data?.error || `Could not load markets (${res.status})`);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to load markets:', err);
          setMarkets([]);
        }
      } finally {
        if (!cancelled) setMarketsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isAdmin, getToken, createdMarket, location.pathname]);

  // Load sales and earnings for selected day/week/month + date (Reports tab)
  useEffect(() => {
    if (!isAdmin || !isReportsView) {
      return;
    }

    let cancelled = false;
    const qs = `period=${encodeURIComponent(reportsPeriod)}&date=${encodeURIComponent(reportsDate)}`;

    (async () => {
      setSalesLoading(true);
      try {
        const res = await api(`/api/admin/sales?${qs}`, { getToken });
        if (!res.ok) throw new Error(`Failed to load sales: ${res.status}`);
        const data = await res.json();
        if (!cancelled) setSales(Array.isArray(data) ? data : []);
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to load sales:', err);
          setSales([]);
        }
      } finally {
        if (!cancelled) setSalesLoading(false);
      }
    })();

    (async () => {
      setEarningsLoading(true);
      try {
        const res = await api(`/api/admin/earnings?${qs}`, { getToken });
        if (!res.ok) throw new Error(`Failed to load earnings: ${res.status}`);
        const data = await res.json();
        if (!cancelled) setEarnings(data);
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to load earnings:', err);
          setEarnings(null);
        }
      } finally {
        if (!cancelled) setEarningsLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [isAdmin, isReportsView, getToken, reportsPeriod, reportsDate]);

  const handleExportCsv = async () => {
    try {
      const qs = `period=${encodeURIComponent(reportsPeriod)}&date=${encodeURIComponent(reportsDate)}`;
      const res = await api(`/api/admin/sales/export-csv?${qs}`, { getToken });
      if (!res.ok) {
        throw new Error(`Failed to export CSV: ${res.status}`);
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `sales-export-${reportsPeriod}-${reportsDate}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError(err.message || 'Failed to export CSV.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);
    setCreatedMarket(null);

    try {
      const res = await api('/api/admin/create-market', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
        getToken,
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        if (res.status === 403) {
          setError('You do not have permission to create markets.');
        } else {
          setError(data.error || `Failed to create market (${res.status})`);
        }
        return;
      }

      setCreatedMarket(data);
      setSuccess(`Market "${data.name}" created successfully!`);
      setFormData({ name: '', code: '', vendorLimit: 10, address: '', daysOpen: '', operatingHours: '' });
    } catch (err) {
      setError(err.message || 'Failed to create market.');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenMarket = async (market) => {
    const marketId = market.id ?? market.Id;
    try {
      const res = await api(`/api/admin/markets/${marketId}`, { getToken });
      if (!res.ok) throw new Error('Failed to load market');
      const data = await res.json();
      setSelectedMarket(data);
      setEditForm({
        name: data.name,
        code: data.code,
        vendorLimit: data.vendorLimit,
        address: data.address ?? '',
        daysOpen: data.daysOpen ?? '',
        operatingHours: data.operatingHours ?? ''
      });
    } catch (err) {
      setError(err.message || 'Failed to load market.');
    }
  };

  const handleCloseMarketModal = () => {
    setSelectedMarket(null);
    setEditForm({ name: '', code: '', vendorLimit: 10, address: '', daysOpen: '', operatingHours: '' });
    setError(null);
    setCopiedOnboardingLink(false);
  };

  const handleCopyOnboardingLink = async () => {
    const marketId = selectedMarket?.id ?? selectedMarket?.Id;
    if (!marketId) return;
    setError(null);
    try {
      const res = await api('/api/invites', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fleaMarketId: marketId, maxUses: 0, expiresInDays: 30 }),
        getToken,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `Failed to create invite (${res.status})`);
      const path = data.invitePath || `/invite/${data.token}`;
      const url = `${window.location.origin}${path}`;
      await navigator.clipboard.writeText(url);
      setCopiedOnboardingLink(true);
      setTimeout(() => setCopiedOnboardingLink(false), 2000);
    } catch (err) {
      setError(err.message || 'Could not create or copy invite link.');
    }
  };

  const handleUpdateMarket = async (e) => {
    e.preventDefault();
    if (!selectedMarket) return;
    const marketId = selectedMarket.id ?? selectedMarket.Id;
    setSavingMarket(true);
    setError(null);
    try {
      const res = await api(`/api/admin/markets/${marketId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editForm),
        getToken,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `Failed to update (${res.status})`);
      setMarkets(prev => prev.map(m => (m.id ?? m.Id) === marketId ? { ...m, ...data } : m));
      handleCloseMarketModal();
    } catch (err) {
      setError(err.message || 'Failed to update market.');
    } finally {
      setSavingMarket(false);
    }
  };

  const handleDeleteMarket = async () => {
    if (!selectedMarket || !window.confirm(`Delete market "${selectedMarket.name ?? selectedMarket.Name}"? This cannot be undone.`)) return;
    const marketId = selectedMarket.id ?? selectedMarket.Id;
    setDeletingMarket(true);
    setError(null);
    try {
      const res = await api(`/api/admin/markets/${marketId}`, { method: 'DELETE', getToken });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `Failed to delete (${res.status})`);
      }
      setMarkets(prev => prev.filter(m => (m.id ?? m.Id) !== marketId));
      handleCloseMarketModal();
    } catch (err) {
      setError(err.message || 'Failed to delete market.');
    } finally {
      setDeletingMarket(false);
    }
  };

  if (checking) {
    return (
      <div className="flex items-center justify-center py-12 text-slate-500 dark:text-slate-400">
        Checking permissions…
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="max-w-md mx-auto p-8 text-center">
        <h2 className="text-2xl font-semibold text-slate-800 dark:text-slate-200 mb-2">Access denied</h2>
        <p className="text-slate-600 dark:text-slate-400">
          You don’t have permission to view this page.
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      <header className="flex items-center justify-between gap-4 mb-6">
        <h1 className="text-4xl font-bold">{isReportsView ? 'Reports' : 'Dashboard'}</h1>
        <button
          type="button"
          onClick={() => signOut({ redirectUrl: '/' })}
          className="shrink-0 text-sm font-medium text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 underline underline-offset-2"
        >
          Sign out
        </button>
      </header>

      {error && (
        <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <p className="text-red-800 dark:text-red-200">{error}</p>
        </div>
      )}

      {/* Dashboard: Create Market + Markets list (route /admin) */}
      {!isReportsView && (
        <>
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-lg p-6 mb-6">
            {!showCreateMarketForm ? (
              <button
                type="button"
                onClick={() => setShowCreateMarketForm(true)}
                className="w-full h-12 text-lg font-semibold rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 transition-colors"
              >
                Create Market
              </button>
            ) : (
              <div className="animate-fade-slide-in relative">
                <button
                  type="button"
                  onClick={() => setShowCreateMarketForm(false)}
                  className="absolute top-0 right-0 w-10 h-10 flex items-center justify-center rounded-lg text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                  aria-label="Close form"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
                <h2 className="text-2xl font-semibold mb-4 pr-12">Create Market</h2>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label htmlFor="name" className="block text-lg font-medium mb-2">Market Name</label>
                    <input
                      id="name"
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      className="w-full h-12 px-4 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
                      placeholder="e.g., Devine Flea Market"
                    />
                  </div>
                  <div>
                    <label htmlFor="code" className="block text-lg font-medium mb-2">Market Code (unique)</label>
                    <input
                      id="code"
                      type="text"
                      value={formData.code}
                      onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                      required
                      className="w-full h-12 px-4 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 font-mono"
                      placeholder="e.g., DEVINE2026"
                      maxLength={50}
                    />
                    <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Internal code (not shared with vendors). Use invite links to onboard booths.</p>
                  </div>
                  <div>
                    <label htmlFor="vendorLimit" className="block text-lg font-medium mb-2">Vendor Limit</label>
                    <input
                      id="vendorLimit"
                      type="number"
                      value={formData.vendorLimit}
                      onChange={(e) => setFormData({ ...formData, vendorLimit: parseInt(e.target.value) || 10 })}
                      required
                      min="1"
                      className="w-full h-12 px-4 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
                    />
                  </div>
                  <div>
                    <label htmlFor="address" className="block text-lg font-medium mb-2">Address</label>
                    <input
                      id="address"
                      type="text"
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      className="w-full h-12 px-4 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
                      placeholder="e.g., 123 Main St, City, State"
                    />
                  </div>
                  <div>
                    <label htmlFor="daysOpen" className="block text-lg font-medium mb-2">Days Open</label>
                    <input
                      id="daysOpen"
                      type="text"
                      value={formData.daysOpen}
                      onChange={(e) => setFormData({ ...formData, daysOpen: e.target.value })}
                      className="w-full h-12 px-4 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
                      placeholder="e.g., Sat & Sun"
                    />
                  </div>
                  <div>
                    <label htmlFor="operatingHours" className="block text-lg font-medium mb-2">Operating Hours</label>
                    <input
                      id="operatingHours"
                      type="text"
                      value={formData.operatingHours}
                      onChange={(e) => setFormData({ ...formData, operatingHours: e.target.value })}
                      className="w-full h-12 px-4 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
                      placeholder="e.g., 8am – 4pm"
                    />
                  </div>
                  {success && createdMarket && (
                    <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                      <p className="text-green-800 dark:text-green-200 font-semibold">{success}</p>
                      <p className="text-green-700 dark:text-green-300 mt-1">Code: <code className="font-mono bg-white dark:bg-slate-800 px-2 py-0.5 rounded">{createdMarket.code}</code></p>
                    </div>
                  )}
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full h-12 text-lg font-semibold rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
                  >
                    {loading ? 'Creating…' : 'Create Market'}
                  </button>
                </form>
              </div>
            )}
          </div>

          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-lg p-6">
            <h2 className="text-2xl font-semibold mb-4">Markets</h2>
            {marketsLoading ? (
              <div className="text-center py-8 text-slate-500">Loading markets…</div>
            ) : markets.length === 0 ? (
              <div className="text-center py-8 text-slate-500">No markets yet. Create one above.</div>
            ) : (
              <ul className="divide-y divide-slate-200 dark:divide-slate-700">
                {markets.map((m) => {
                  const id = m.id ?? m.Id;
                  const name = m.name ?? m.Name ?? 'Unnamed';
                  const code = m.code ?? m.Code ?? '';
                  const current = m.currentVendorCount ?? m.CurrentVendorCount ?? 0;
                  const limit = m.vendorLimit ?? m.VendorLimit ?? 0;
                  return (
                    <li key={id}>
                      <button
                        type="button"
                        onClick={() => handleOpenMarket(m)}
                        className="w-full text-left py-4 px-3 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700/50 transition-colors"
                      >
                        <div className="font-semibold text-lg text-slate-900 dark:text-slate-100">{name}</div>
                        <div className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                          Code: <span className="font-mono">{code}</span> · Vendors: {current} / {limit}
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </>
      )}

      {/* Reports: Sales & Earnings (route /admin/reports) */}
      {isReportsView && (
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-lg p-6">
          <div className="flex flex-col gap-4 mb-4 sm:flex-row sm:justify-between sm:items-center">
            <h2 className="text-2xl font-semibold">Sales & Earnings</h2>
            <button
              type="button"
              onClick={handleExportCsv}
              className="text-lg font-semibold text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 underline underline-offset-2 hover:no-underline self-start sm:self-auto"
            >
              Export to CSV
            </button>
          </div>

          <div className="flex flex-col gap-3 mb-4 sm:flex-row sm:items-end sm:justify-between">
            <div className="flex gap-2 flex-wrap">
              {['day', 'week', 'month'].map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setReportsPeriod(p)}
                  className={`px-4 py-2 rounded-lg capitalize ${
                    reportsPeriod === p
                      ? 'bg-indigo-600 text-white'
                      : 'bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300'
                  }`}
                >
                  {p}
                </button>
              ))}
            </div>
            <label className="flex flex-col gap-1 text-sm font-medium text-slate-700 dark:text-slate-300">
              <span>
                {reportsPeriod === 'day'
                  ? 'Pick a day'
                  : reportsPeriod === 'week'
                    ? 'Any day in that week'
                    : 'Any day in that month'}
              </span>
              <input
                type="date"
                value={reportsDate}
                onChange={(e) => setReportsDate(e.target.value || todayLocalIso())}
                className="h-11 rounded-lg border-2 border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 text-base"
              />
            </label>
          </div>
          <p className="text-center text-sm font-medium text-slate-500 mb-4">
            {earnings?.periodLabel ||
              (reportsPeriod === 'week'
                ? 'Selected week'
                : reportsPeriod === 'month'
                  ? 'Selected month'
                  : 'Selected day')}
          </p>

          {earningsLoading ? (
            <div className="text-center py-8 text-slate-500">Loading earnings…</div>
          ) : earnings ? (
            <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                <div className="text-sm text-blue-700 dark:text-blue-300">Total Sales</div>
                <div className="text-2xl font-bold text-blue-900 dark:text-blue-100">
                  {(earnings.totalSalesCount ?? 0).toLocaleString()}
                </div>
              </div>
              <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
                <div className="text-sm text-green-700 dark:text-green-300">Total Gross Sales</div>
                <div className="text-2xl font-bold text-green-900 dark:text-green-100">
                  ${(earnings.totalGrossSales ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
              </div>
              <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg border-2 border-purple-300 dark:border-purple-700">
                <div className="text-sm text-purple-700 dark:text-purple-300">Platform Earnings (3%)</div>
                <div className="text-3xl font-bold text-purple-900 dark:text-purple-100">
                  ${(earnings.totalPlatformEarnings ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
              </div>
            </div>
          ) : null}
          {salesLoading ? (
            <div className="text-center py-8 text-slate-500">Loading sales…</div>
          ) : sales.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse text-sm md:text-base">
                <thead>
                  <tr className="border-b-2 border-slate-300 dark:border-slate-600">
                    <th className="p-3 font-semibold">Date</th>
                    <th className="p-3 font-semibold">Item</th>
                    <th className="p-3 font-semibold">Vendor</th>
                    <th className="p-3 font-semibold">Market</th>
                    <th className="p-3 font-semibold">Amount</th>
                    <th className="p-3 font-semibold">Platform Fee</th>
                    <th className="p-3 font-semibold">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {sales.map((sale) => (
                    <tr key={sale.id} className="border-b border-slate-200 dark:border-slate-700">
                      <td className="p-3">{new Date(sale.createdAt).toLocaleDateString()}</td>
                      <td className="p-3"><div className="font-medium">{sale.itemTitle}</div><div className="text-xs text-slate-500">ID: {sale.itemId}</div></td>
                      <td className="p-3">{sale.vendorStoreName}</td>
                      <td className="p-3">{sale.marketName}</td>
                      <td className="p-3 font-medium">${sale.amount.toFixed(2)}</td>
                      <td className="p-3 text-purple-600 dark:text-purple-400">${sale.platformFee.toFixed(2)}</td>
                      <td className="p-3">
                        <span className={`px-2 py-1 rounded text-xs ${sale.status === 'picked_up' ? 'bg-green-100 dark:bg-green-900/30 text-green-800' : sale.status === 'paid' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-800' : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800'}`}>
                          {sale.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500">No sales for this period.</div>
          )}
        </div>
      )}

      {/* Small alert: onboarding link copied */}
      {copiedOnboardingLink && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 pointer-events-none" aria-live="polite">
          <div className="bg-slate-800 dark:bg-slate-700 text-white px-6 py-4 rounded-xl shadow-lg text-center font-medium">
            Onboarding link copied
          </div>
        </div>
      )}

      {/* Edit/Delete Market modal — max height leaves room for bottom nav so content isn't cut off */}
      {selectedMarket && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 pb-20 sm:pb-4 bg-black/50" onClick={handleCloseMarketModal}>
          <div className="bg-white dark:bg-slate-800 rounded-xl rounded-b-none sm:rounded-b-xl shadow-xl max-w-lg w-full max-h-[calc(100dvh-5.5rem)] overflow-y-auto overflow-x-hidden p-6" onClick={e => e.stopPropagation()}>
            <h3 className="text-xl font-bold mb-4">Edit Market: {selectedMarket.name ?? selectedMarket.Name}</h3>

            {/* Copy onboarding link for vendors */}
            <div className="mb-5">
              <button
                type="button"
                onClick={handleCopyOnboardingLink}
                className="w-full flex items-center justify-center gap-2 h-12 px-4 rounded-xl font-semibold bg-indigo-600 text-white hover:bg-indigo-700 active:bg-indigo-800 transition-colors"
              >
                <span aria-hidden>📋</span>
                Copy vendor invite link
              </button>
            </div>

            <form onSubmit={handleUpdateMarket} className="space-y-4 pb-4">
              <div>
                <label className="block text-sm font-medium mb-1">Name</label>
                <input
                  type="text"
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  className="w-full h-10 px-3 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Code</label>
                <input
                  type="text"
                  value={editForm.code}
                  onChange={(e) => setEditForm({ ...editForm, code: e.target.value.toUpperCase() })}
                  className="w-full h-10 px-3 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 font-mono"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Vendor Limit (current: {selectedMarket.currentVendorCount})</label>
                <input
                  type="number"
                  min={selectedMarket.currentVendorCount}
                  value={editForm.vendorLimit}
                  onChange={(e) => setEditForm({ ...editForm, vendorLimit: parseInt(e.target.value) || 1 })}
                  className="w-full h-10 px-3 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700"
                />
              </div>
              <div className="min-w-0">
                <label className="block text-sm font-medium mb-1">Address</label>
                <input
                  type="text"
                  value={editForm.address}
                  onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
                  className="w-full min-w-0 h-10 px-3 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 box-border"
                  placeholder="e.g., 123 Main St, City, State"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Days Open</label>
                <input
                  type="text"
                  value={editForm.daysOpen}
                  onChange={(e) => setEditForm({ ...editForm, daysOpen: e.target.value })}
                  className="w-full h-10 px-3 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700"
                  placeholder="e.g., Sat & Sun"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Operating Hours</label>
                <input
                  type="text"
                  value={editForm.operatingHours}
                  onChange={(e) => setEditForm({ ...editForm, operatingHours: e.target.value })}
                  className="w-full h-10 px-3 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700"
                  placeholder="e.g., 8am – 4pm"
                />
              </div>
              {selectedMarket.vendors && selectedMarket.vendors.length > 0 && (
                <div className="text-sm text-slate-600 dark:text-slate-400">
                  Vendors in this market: {selectedMarket.vendors.map(v => v.storeName || `#${v.id}`).join(', ')}
                </div>
              )}
              <div className="flex gap-3 pt-2">
                <button type="submit" disabled={savingMarket} className="flex-1 h-11 font-semibold rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50">
                  {savingMarket ? 'Saving…' : 'Save'}
                </button>
                <button type="button" onClick={handleCloseMarketModal} className="h-11 px-4 rounded-lg border border-slate-300 dark:border-slate-600">
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleDeleteMarket}
                  disabled={deletingMarket || (selectedMarket.currentVendorCount > 0)}
                  className="h-11 px-4 font-semibold rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  title={selectedMarket.currentVendorCount > 0 ? 'Remove all vendors first' : 'Delete market'}
                >
                  {deletingMarket ? 'Deleting…' : 'Delete'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
